import { EventEmitter } from 'events';
import { useCallback, useEffect, useRef } from 'react';

/**
 * A memoized React hook that enables continuous event publishing via an EventEmitter.
 * This is a push-based approach where events are continuously emitted.
 * This publisher also emits the current state to new subscribers.
 *
 * @template EventType - The type of the event payload.
 *
 * @param {EventEmitter} eventBus - The EventEmitter instance used for communication.
 * @param {string} eventName - The event name to emit when publishing events.
 * @param {string} subscriptionEventName - The event name for subscription notifications.
 * @param {EventType} initialState - Initial state value.
 *
 * @returns {(eventPayload: EventType) => void} A memoized function to publish an event.
 *
 * @example
 * const publishIsPlayingEvent = useEventBusPublisherMemoized<boolean>(
 *   eventBus,
 *   'myEvent',
 *   'myEventSubscription',
 *   { isPlaying: false }
 * );
 *
 * // Publish an event
 * publishIsPlayingEvent(true);
 */
export function useEventBusPublisherMemoized<EventType>(
  eventBus: EventEmitter,
  eventName: string,
  subscriptionEventName: string,
  initialState: EventType
) {
  const stateRef = useRef<EventType>(initialState);

  const publishEvent = useCallback(
    (eventPayload: EventType) => {
      stateRef.current = eventPayload;
      eventBus.emit(eventName, eventPayload);
    },
    [eventBus, eventName]
  );

  // Listen for subscription events and emit current state to new subscribers
  useEffect(() => {
    const handleSubscription = (data: { eventName: string; action: string }) => {
      if (data.eventName === eventName) {
        // If someone subscribed, emit the current state immediately
        // this also emits to the current subscribers, can be improved to only emit to new subscribers
        if (data.action === 'subscribed') {
          eventBus.emit(eventName, stateRef.current);
        }
      }
    };

    eventBus.on(subscriptionEventName, handleSubscription);

    return () => {
      eventBus.off(subscriptionEventName, handleSubscription);
    };
  }, [eventBus, eventName, subscriptionEventName]);

  return publishEvent;
}
